import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  alpha,
} from "@mui/material";
import { validateStep2Inputs } from "../utils/validation";
import { styleForTextField } from "../../../../../utils/TextField/TextField";
import {
  LIMITATION_TYPES,
  isCleanValue,
} from "../../../../../../utils/static/limitation";
import { StyledMenuItem } from "../../../../../utils/GroupedSelect";
import { getCaSettingByKey } from "../../../../../../builders/settings/CaSetting";
import {
  getDefaultInitialAllowanceRate,
  hasInitialAllowance,
} from "../../CreateEditAsset";

const CapitalAllowanceForm = ({
  onSubmit,
  assetCost,
  schedule,
  caTypeOptions,
  caSettings,
}) => {
  const [formData, setFormData] = useState({
    fyEndMonth: "",
    caType: "",
    assetCode: "",
    assetName: "",
    numberOfYears: "",
    initialAllowanceRate: "",
  });

  const [errors, setErrors] = useState([]);

  const showIaRate = hasInitialAllowance(caSettings, formData.caType);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "caType") {
      setFormData({
        ...formData,
        [name]: value,
        numberOfYears: getCaSettingByKey(caSettings, value)?.numberOfYears || 1,
        initialAllowanceRate: hasInitialAllowance(caSettings, value)
          ? getDefaultInitialAllowanceRate(value, caSettings)
          : "",
      });
    } else if (name === "numberOfYears") {
      if (isCleanValue(value, LIMITATION_TYPES.ONLY_NUMBERS)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value === "" ? "" : parseInt(value, 10) || "",
        }));
      }
    } else if (name === "fyEndMonth") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? "" : parseInt(value, 10),
      }));
    } else if (name === "initialAllowanceRate") {
      const twoDecimalRegex = /^\d*(\.\d{0,2})?$/;
      if (value === "" || twoDecimalRegex.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "," || e.key === "e" || e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = validateStep2Inputs(formData);
    const missingFields = [...validation.missingFields];

    if (showIaRate) {
      if (
        formData.initialAllowanceRate === "" ||
        formData.initialAllowanceRate === null ||
        formData.initialAllowanceRate === undefined
      ) {
        missingFields.push("Initial Allowance Rate");
      } else {
        const rate = parseFloat(formData.initialAllowanceRate);
        if (isNaN(rate) || rate < 0 || rate > 100) {
          missingFields.push(
            "Initial Allowance Rate (must be a percentage between 0-100)"
          );
        }
      }
    }

    if (missingFields.length > 0) {
      setErrors(missingFields);
      return;
    }

    setErrors([]);

    onSubmit({
      ...formData,
      initialAllowanceRate: showIaRate
        ? parseFloat(formData.initialAllowanceRate)
        : undefined,
      assetCost,
      schedule,
    });
  };

  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNumber - 1] || "";
  };

  const isFormValid = () => {
    const { fyEndMonth, caType, assetName, initialAllowanceRate } = formData;
    const baseValid = fyEndMonth !== "" && caType !== "" && assetName !== "";
    if (!baseValid) return false;
    if (!showIaRate) return true;
    if (initialAllowanceRate === "" || initialAllowanceRate === null) {
      return false;
    }
    const rate = parseFloat(initialAllowanceRate);
    return !isNaN(rate) && rate >= 0 && rate <= 100;
  };

  return (
    <Stack spacing={4}>
      {errors.length > 0 && (
        <Alert
          severity='error'
          sx={{
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
          }}
        >
          <Typography variant='subtitle2' gutterBottom>
            <strong>Please provide the following missing fields:</strong>
          </Typography>
          <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.5rem" }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Box component='form' onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            label='Asset Name'
            name='assetName'
            value={formData.assetName}
            onChange={handleChange}
            required
            fullWidth
            autoComplete='off'
            error={errors.some((e) => e.toLowerCase().includes("asset name"))}
            helperText={
              errors.some((e) => e.toLowerCase().includes("asset name"))
                ? "Please enter an asset name"
                : "Descriptive name for this asset"
            }
            sx={styleForTextField}
          />

          <TextField
            select
            label='Company Financial Year-End Month'
            name='fyEndMonth'
            value={formData.fyEndMonth}
            onChange={handleChange}
            required
            fullWidth
            autoComplete='off'
            error={errors.some((e) =>
              e.toLowerCase().includes("financial year")
            )}
            helperText={
              errors.some((e) => e.toLowerCase().includes("financial year"))
                ? "Please select a financial year-end month"
                : "Select the month when your company's financial year ends"
            }
            sx={styleForTextField}
          >
            <StyledMenuItem value=''>Select month</StyledMenuItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <StyledMenuItem key={month} value={month}>
                {getMonthName(month)}
              </StyledMenuItem>
            ))}
          </TextField>

          <TextField
            select
            label='Capital Allowance Type'
            name='caType'
            value={formData.caType}
            onChange={handleChange}
            required
            fullWidth
            autoComplete='off'
            error={errors.some((e) => e.toLowerCase().includes("ca type"))}
            helperText={
              errors.some((e) => e.toLowerCase().includes("ca type"))
                ? "Please select a CA type"
                : "Select the type of capital allowance"
            }
            sx={styleForTextField}
          >
            <StyledMenuItem value=''>Select CA Type</StyledMenuItem>
            {caTypeOptions.map((ca) => (
              <StyledMenuItem key={ca} value={ca}>
                {ca}
              </StyledMenuItem>
            ))}
          </TextField>

          <TextField
            label='Number of Years of Claims'
            name='numberOfYears'
            type='number'
            value={formData.numberOfYears}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            required
            fullWidth
            autoComplete='off'
            error={errors.some((e) =>
              e.toLowerCase().includes("number of years")
            )}
            helperText={
              errors.some((e) => e.toLowerCase().includes("number of years"))
                ? "Please enter the number of years"
                : "Number of years for capital allowance claims"
            }
            InputProps={{
              inputProps: {
                step: "1",
                min: "1",
                inputMode: "numeric",
                pattern: "[0-9]*",
              },
              "aria-label": "Number of Years",
            }}
            sx={styleForTextField}
            disabled={
              getCaSettingByKey(caSettings, formData["caType"])
                ?.numberOfYears !== null
            }
          />

          {showIaRate && (
            <TextField
              label='Initial Allowance Rate'
              name='initialAllowanceRate'
              type='number'
              value={formData.initialAllowanceRate}
              onChange={handleChange}
              required
              fullWidth
              autoComplete='off'
              error={errors.some((e) =>
                e.toLowerCase().includes("initial allowance")
              )}
              helperText={
                errors.some((e) =>
                  e.toLowerCase().includes("initial allowance")
                )
                  ? "Please enter a valid percentage (0-100)"
                  : "Enter the initial allowance rate as a percentage (0-100)"
              }
              InputProps={{
                endAdornment: (
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{ ml: 1 }}
                  >
                    %
                  </Typography>
                ),
                inputProps: { min: 0, max: 100, step: "0.01" },
                "aria-label": "Initial Allowance Rate",
              }}
              sx={styleForTextField}
            />
          )}

          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={!isFormValid()}
            sx={{ alignSelf: "flex-start", minWidth: 200 }}
          >
            Generate Capital Allowance Upload Table
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export default CapitalAllowanceForm;
